import { BaseService, ServiceError } from './BaseService';
import type { ApiResponse } from '@/types';

// ============================================
// Types
// ============================================

export interface Contact {
    id: number;
    name: string;
    email: string | null;
    phone: string | null;
    companyId: number | null;
    companyName?: string;
    notes: string | null;
    isActive: boolean;
    createdAt?: string;
    createdBy?: number;
    updatedAt?: string;
    updatedBy?: number;
}

export interface ContactCreateData {
    name: string;
    email?: string | null;
    phone?: string | null;
    companyId?: number | null;
    notes?: string | null;
}

export interface ContactUpdateData {
    name?: string;
    email?: string | null;
    phone?: string | null;
    companyId?: number | null;
    notes?: string | null;
    isActive?: boolean;
}

export interface ContactListFilters {
    search?: string;
    companyId?: number;
    isActive?: boolean;
    page?: number;
    limit?: number;
}

export interface ContactListResponse {
    contacts: Contact[];
    pagination: {
        page: number;
        limit: number;
        total: number;
    };
}

export interface RequestResultRecipient {
    id: number;
    requestId: number;
    contactId: number;
    contact: Contact;
    addedAt: string;
    addedBy: number;
    addedByName?: string;
    removedAt: string | null;
    removedBy: number | null;
    notifiedAt: string | null;
    notifiedBy: number | null;
    notifiedByName?: string;
    notificationMethod: string | null;
    notificationStatus: string | null;
    notificationNotes: string | null;
}

export interface NotificationData {
    method: 'email' | 'sms' | 'phone' | 'manual';
    status?: 'pending' | 'sent' | 'delivered' | 'failed';
    notes?: string;
}

// ============================================
// Service
// ============================================

class ContactService extends BaseService {
    constructor() {
        super('ContactService');
    }

    // ============================================
    // Contacts CRUD
    // ============================================

    /**
     * List contacts with filtering and pagination
     */
    async listContacts(filters?: ContactListFilters): Promise<ApiResponse<ContactListResponse>> {
        const url = this.buildUrl('/contacts', {
            search: filters?.search,
            companyId: filters?.companyId,
            isActive: filters?.isActive,
            page: filters?.page || 1,
            limit: filters?.limit || 50
        });
        return this.get<ContactListResponse>(url);
    }

    /**
     * Search contacts by name/email/phone (for autocomplete)
     */
    async searchContacts(query: string, limit: number = 10): Promise<Contact[]> {
        const response = await this.listContacts({
            search: query,
            isActive: true,
            limit
        });
        return response.data?.contacts || [];
    }

    /**
     * Get a single contact by ID
     */
    async getContact(id: number): Promise<ApiResponse<Contact>> {
        this.validateRequired({ id });
        return this.get<Contact>(`/contacts/${id}`);
    }

    /**
     * Create a new contact
     */
    async createContact(data: ContactCreateData): Promise<ApiResponse<Contact>> {
        this.validateRequired({ name: data.name });
        return this.post<Contact>('/contacts', data);
    }

    /**
     * Update a contact
     */
    async updateContact(id: number, data: ContactUpdateData): Promise<ApiResponse<Contact>> {
        this.validateRequired({ id });
        return this.put<Contact>(`/contacts/${id}`, data);
    }

    /**
     * Delete (soft-delete) a contact
     */
    async deleteContact(id: number): Promise<ApiResponse<void>> {
        this.validateRequired({ id });
        return this.delete<void>(`/contacts/${id}`);
    }

    /**
     * Get requests associated with a contact
     */
    async getContactRequests(contactId: number): Promise<ApiResponse<any[]>> {
        this.validateRequired({ contactId });
        return this.get<any[]>(`/contacts/${contactId}/requests`);
    }

    // ============================================
    // Request Result Recipients
    // ============================================

    /**
     * Get recipients for a request
     */
    async getRequestRecipients(requestId: number): Promise<ApiResponse<RequestResultRecipient[]>> {
        this.validateRequired({ requestId });
        return this.get<RequestResultRecipient[]>(`/requests/${requestId}/result-recipients`);
    }

    /**
     * Add recipient(s) to a request
     */
    async addRequestRecipient(
        requestId: number,
        contactId: number | number[]
    ): Promise<ApiResponse<RequestResultRecipient | RequestResultRecipient[]>> {
        this.validateRequired({ requestId, contactId });

        const payload = Array.isArray(contactId)
            ? { contactIds: contactId }
            : { contactId };

        return this.post(`/requests/${requestId}/result-recipients`, payload);
    }

    /**
     * Remove a recipient from a request
     */
    async removeRequestRecipient(requestId: number, contactId: number): Promise<ApiResponse<void>> {
        this.validateRequired({ requestId, contactId });
        return this.delete<void>(`/requests/${requestId}/result-recipients/${contactId}`);
    }

    /**
     * Mark a recipient as notified
     */
    async markRecipientNotified(
        requestId: number,
        contactId: number,
        data: NotificationData
    ): Promise<ApiResponse<RequestResultRecipient>> {
        this.validateRequired({ requestId, contactId, method: data.method });
        return this.post<RequestResultRecipient>(
            `/requests/${requestId}/result-recipients/${contactId}/notified`,
            data
        );
    }

    /**
     * Get notification history for a request (includes removed recipients)
     */
    async getRecipientHistory(requestId: number): Promise<ApiResponse<RequestResultRecipient[]>> {
        this.validateRequired({ requestId });
        return this.get<RequestResultRecipient[]>(`/requests/${requestId}/result-recipients/history`);
    }
}

// Export singleton instance
const contactService = new ContactService();
export default contactService;

// Also export the class for testing
export { ContactService };